import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  OutlinedInput,
  ListItemText,
  SelectChangeEvent,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import ExperienceStatistics from "./experience-statistics";
import ServiceCommentsTable from "./service-comments";
import EmployeesCommentsTable from "./employees-comments";
import { useGetCitiesList } from "src/api/general";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { ICity } from "src/types/generic";
import { useGetLocationsList } from "src/api/locations";
import { useGetClientsList } from "src/api/clients";
import { IClientResponse } from "src/types/clients";
import { LoadingButton } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers";
import { notificationsState } from "src/store/notifications";
import { useRecoilState } from "recoil";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const rateOptions = ["1", "2", "3", "4", "5"];

const minDate = new Date("2024-01-01T00:00:00");
const maxDate = moment().add(1, "year").endOf("year").toDate();

const CustomersFeedback = () => {
  const [openStartDate, setOpenStartDate] = useState<boolean>(false);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  const [openEndDate, setOpenEndDate] = useState<boolean>(false);
  const [tempSelectedLocations, setTempSelectedLocations] = useState<any[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<any[]>([]);
  const [tempSelectedCity, setTempSelectedCity] = useState<ICity>();
  const [selectedCity, setSelectedCity] = useState<ICity>();
  const [tempRates, setTempRates] = useState<string[]>([]);
  const [rates, setRates] = useState<string[]>([]);
  const [selectedClient, setSelectedClient] = useState<IClientResponse>();
  const [tempSelectedClient, setTempSelectedClient] =
    useState<IClientResponse>();
  const [searchBy, setSearchBy] = useState<string>("");
  const [tempSearchBy, setTempSearchBy] = useState<string>("");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [tempSelectedService, setTempSelectedService] = useState<string>("all");
  const [selectedService, setSelectedService] = useState<string>("all");

  // APIS
  const { data: citiesData } = useGetCitiesList({
    type: "active",
  });
  const { data: locationsData } = useGetLocationsList();
  const { data: clientsData } = useGetClientsList();

  const handleChangeLocations = (value: any[]) => {
    setTempSelectedLocations(value);
  };

  const handleChangeRates = (event: SelectChangeEvent<typeof tempRates>) => {
    const {
      target: { value },
    } = event;
    setTempRates(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value,
    );
  };

  const handleChangeTypeOfService = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setTempSelectedService(value);
  };

  const handleSearch = (searchedVal: string) => {
    // setPage(0);
    setTempSearchBy(searchedVal);
  };

  const clearSearch = () => {
    setTempSearchBy("");
    if (searchBy !== "") {
      setSearchBy("");
      // setPage(0);
    }
  };

  const handleApply = () => {
    if (
      tempStartDate !== null &&
      tempEndDate !== null &&
      tempStartDate > tempEndDate
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: "Start date must be before end date",
        },
      ]);
      return;
    }
    setSelectedCity(tempSelectedCity);
    setEndDate(tempEndDate);
    setStartDate(tempStartDate);
    setRates(tempRates);
    setSelectedLocations(tempSelectedLocations);
    setSelectedClient(tempSelectedClient);
    setSearchBy(tempSearchBy);
    setSelectedService(tempSelectedService);
  };

  const handleResetFilers = () => {
    setEndDate(null);
    setStartDate(null);
    setSelectedLocations([]);
    setSelectedClient(undefined);
    setSelectedCity(undefined);
    setRates([]);
    setSearchBy("");
    setSelectedService("all");

    setTempEndDate(null);
    setTempStartDate(null);
    setTempSelectedLocations([]);
    setTempSelectedClient(undefined);
    setTempSelectedCity(undefined);
    setTempRates([]);
    setTempSearchBy("");
    setTempSelectedService("all");
  };

  const handleChangeClient = (event: React.SyntheticEvent, newValue: any) => {
    setTempSelectedClient(newValue);
  };

  const handleChangeCity = (event: React.SyntheticEvent, newValue: any) => {
    setTempSelectedCity(newValue);
  };

  useEffect(() => {
    setTempSelectedLocations([]);
    setTempSelectedCity(undefined);
  }, [tempSelectedClient]);

  useEffect(() => {
    setTempSelectedCity(undefined);
  }, [tempSelectedLocations]);

  const locationsIds = useMemo(
    () => selectedLocations.map((location) => location.id),
    [selectedLocations],
  );

  const sortedLocations = useMemo(() => {
    return (
      locationsData?.locations.sort((a: any, b: any) =>
        a.nameEn?.localeCompare(b.nameEn),
      ) ?? []
    );
  }, [locationsData]);

  const sortedClients = useMemo(() => {
    return (
      clientsData?.data
        .slice()
        .sort((a, b) =>
          a.clientInfo.nameEn?.localeCompare(b.clientInfo.nameEn),
        ) || []
    );
  }, [clientsData]);

  const filteredLocations = useMemo(() => {
    return tempSelectedClient
      ? sortedLocations.filter(
          (loc: any) => loc.clientId === tempSelectedClient?.id,
        )
      : sortedLocations;
  }, [tempSelectedClient, sortedLocations]);

  const filteredCities = useMemo(() => {
    if (!citiesData?.data) return [];

    if (tempSelectedLocations.length === 0 && !tempSelectedClient) {
      return citiesData.data;
    }

    const availableLocations =
      tempSelectedLocations.length > 0
        ? tempSelectedLocations
        : filteredLocations;

    const availableLocationsCitiesIds = availableLocations.map(
      (loc: any) => loc.cityId,
    );

    return citiesData.data.filter((city) =>
      availableLocationsCitiesIds.includes(city.id),
    );
  }, [
    tempSelectedLocations,
    citiesData,
    filteredLocations,
    tempSelectedClient,
  ]);

  const dateRange = useMemo(() => {
    return {
      startDate,
      endDate,
    };
  }, [startDate, endDate]);

  console.log({ filteredLocations, filteredCities });

  return (
    <Box p={"30px"}>
      <Typography fontSize={"22px"} fontWeight={600} color={"#F0F0F0"} mb={4}>
        Customers Feedback
      </Typography>
      <Stack
        mt={3}
        mb={"29px"}
        direction={{ xs: "column", md: "row" }}
        gap={"10px"}
      >
        <Stack direction={"row"} gap={"10px"} flexWrap={"wrap"} flexGrow={1}>
          <TextField
            size="small"
            placeholder="Search"
            value={tempSearchBy}
            onChange={(event) => handleSearch(event.target.value)}
            sx={{ width: { xs: "100%", sm: "250px" } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ opacity: !tempSearchBy ? 0 : 1 }}
                >
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* client */}
          <CustomAutocomplete
            key={tempSelectedClient?.id ?? "client-autocomplete"}
            sx={{ width: { xs: "100%", sm: "220px" } }}
            size="small"
            disablePortal
            id="combo-box-demo"
            options={sortedClients}
            getOptionLabel={(option) => option.clientInfo.nameEn}
            getOptionKey={(option) => `${option.id}`}
            value={tempSelectedClient}
            onChange={handleChangeClient}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"Client"} />
            )}
          />
          {/* locations */}
          <CustomAutocomplete
            sx={{ width: { xs: "100%", sm: "220px" } }}
            size="small"
            limitTags={1}
            multiple
            id="tags-outlined"
            options={filteredLocations}
            disableCloseOnSelect
            getOptionLabel={(option: any) => option.nameEn}
            getOptionKey={(option: any) => option.id}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={`${props.id} ${option.id}`}>
                <Checkbox
                  size="small"
                  icon={icon}
                  checkedIcon={checkedIcon}
                  sx={{ padding: "3px 0px", mr: "5px" }}
                  checked={selected}
                />
                <Typography>{option.nameEn}</Typography>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Location" placeholder="" />
            )}
            value={tempSelectedLocations}
            onChange={(event, value) => {
              handleChangeLocations(value);
            }}
          />
          {/* city */}
          <CustomAutocomplete
            key={tempSelectedCity?.id ?? "city-autocomplete"}
            sx={{ width: { xs: "100%", sm: "170px" } }}
            size="small"
            disablePortal
            id="combo-box-demo"
            options={filteredCities || []}
            getOptionLabel={(option) => option.nameEn}
            getOptionKey={(option) => `${option.id}`}
            value={tempSelectedCity}
            onChange={handleChangeCity}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"City"} />
            )}
          />
          {/* service */}
          <FormControl
            fullWidth
            size="small"
            sx={{ width: { xs: "100%", sm: "190px" } }}
          >
            <InputLabel id="demo-simple-select-label">Service</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempSelectedService}
              label="Service"
              onChange={handleChangeTypeOfService}
              fullWidth
            >
              <MenuItem value={"all"}>All</MenuItem>
              <MenuItem value={"valet"}>Valet</MenuItem>
              <MenuItem value={"partialCarWash"}>Partial Car Wash</MenuItem>
              <MenuItem value={"fullCarWash"}>Complete Car Wash</MenuItem>
            </Select>
          </FormControl>
          {/* rates */}
          <FormControl
            size="small"
            fullWidth
            sx={{
              width: { xs: "100%", sm: "140px" },
            }}
          >
            <InputLabel id="demo-multiple-checkbox-label">Rates</InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              multiple
              value={tempRates}
              onChange={handleChangeRates}
              input={<OutlinedInput label="Rates" />}
              renderValue={(selected) => selected.join(", ")}
            >
              {rateOptions.map((rate) => (
                <MenuItem key={rate} value={rate}>
                  <Checkbox checked={tempRates.indexOf(rate) > -1} />
                  <ListItemText primary={rate} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* start date */}
          <DatePicker
            label="Start Date"
            slotProps={{
              textField: {
                size: "small",
                onClick: () => setOpenStartDate(true),
                sx: {
                  width: "126px",
                },
                inputProps: { readOnly: true },
              },
            }}
            format={"dd/MM/yyyy"}
            value={tempStartDate}
            onChange={(newValue) => setTempStartDate(newValue)}
            monthsPerRow={4}
            name="start date"
            disableOpenPicker
            open={openStartDate}
            onClose={() => setOpenStartDate(false)}
            minDate={minDate}
            maxDate={maxDate}
          />
          {/* end date */}
          <DatePicker
            label="End Date"
            slotProps={{
              textField: {
                size: "small",
                onClick: () => setOpenEndDate(true),
                sx: {
                  width: "126px",
                },
                inputProps: { readOnly: true },
              },
            }}
            format={"dd/MM/yyyy"}
            value={tempEndDate}
            onChange={(newValue) => setTempEndDate(newValue)}
            name="end date"
            disableOpenPicker
            open={openEndDate}
            onClose={() => setOpenEndDate(false)}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Stack>
        <Stack direction={"row"}>
          <LoadingButton onClick={handleResetFilers}>Clear</LoadingButton>
          <LoadingButton
            variant="contained"
            onClick={handleApply}
            sx={{ width: "100px" }}
          >
            Apply
          </LoadingButton>
        </Stack>
      </Stack>

      <ExperienceStatistics
        cityId={selectedCity?.id as string}
        locations={locationsIds}
        dateRange={dateRange}
        rates={rates}
        clientId={selectedClient?.id as string}
        searchBy={searchBy}
        selectedService={selectedService}
      />

      <Box my={2}>
        <ServiceCommentsTable
          cityId={selectedCity?.id as string}
          locations={locationsIds}
          dateRange={dateRange}
          rates={rates}
          clientId={selectedClient?.id as string}
          searchBy={searchBy}
          selectedService={selectedService}
        />
      </Box>

      <Box>
        <EmployeesCommentsTable
          cityId={selectedCity?.id as string}
          locations={locationsIds}
          dateRange={dateRange}
          rates={rates}
          clientId={selectedClient?.id as string}
          searchBy={searchBy}
          selectedService={selectedService}
        />
      </Box>
    </Box>
  );
};

export default CustomersFeedback;
